import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import Sheet from '../../common/Sheet';
import SignaturePad from '../../common/SignaturePad';
import { PatientDocumentsService } from '../../../services/patientmr/patientDocuments.service';
import { ApiError } from '../../../dtos/common/api.dto';
import { LEGAL_AUTHORITY_OPTIONS } from '../../../constants/patientLifecycle';
import {
  CONSENT_PACKET_TYPES,
  PACKET_SERVICES,
  PATIENT_DOCUMENT_TYPE_LABELS,
} from '../../../dtos/patientmr/patientDocuments.dto';
import type {
  AdmissionPacketItem,
  AdmissionPacketSignerInput,
  AdmissionPacketTemplate,
  PacketService,
  PatientDocument,
} from '../../../dtos/patientmr/patientDocuments.dto';

/** A signer choice: a recorded legal guardian (authority from the guardian record, read-only) */
export interface PacketSignerOption {
  guardianId: string;
  name: string;
  relationship: string;
  legalAuthority?: string;
  canConsent: boolean;
}

export interface SignPacketDocumentSheetProps {
  open: boolean;
  patientId: string;
  item: AdmissionPacketItem | null;
  template: AdmissionPacketTemplate | null;
  guardians: PacketSignerOption[];
  /** Patient's date of birth; a patient under 18 cannot sign for themself */
  patientDob?: string;
  patientName?: string;
  /** The logged-in user, recorded by the server as the witness */
  witnessName: string;
  onClose: () => void;
  onSigned: (doc: PatientDocument) => void;
}

const PATIENT_SIGNER = '__PATIENT__';

export const ageInYears = (dob: string | undefined, now = new Date()): number | null => {
  const t = Date.parse(dob || '');
  if (Number.isNaN(t)) return null;
  return (now.getTime() - t) / (365.25 * 86400000);
};

const authorityLabel = (code?: string): string =>
  LEGAL_AUTHORITY_OPTIONS.find((o) => o.value === code)?.label || code || 'Not recorded';

const signErrorMessage = (err: unknown): string => {
  const code = err instanceof ApiError ? err.domainCode : undefined;
  if (code === 'SIGNER_NOT_AUTHORIZED') {
    return err instanceof Error && err.message ? err.message : 'This person is not recorded as able to consent for the patient.';
  }
  if (code === 'SIGNATURE_REQUIRED' || code === 'SIGNATURE_INVALID') return 'Draw the signature inside the box, then submit again.';
  if (code === 'SIGNATURE_TOO_LARGE') return 'The signature is too large. Clear it and sign again.';
  return err instanceof Error && err.message ? err.message : 'The document was not signed. Try again.';
};

const choiceClass = (active: boolean, disabled: boolean) =>
  `flex min-h-[44px] w-full items-start gap-3 rounded-lg border-2 px-3 py-2 text-left ${
    disabled ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60'
      : active ? 'border-slate-800 bg-slate-50' : 'border-slate-300 bg-white hover:border-slate-400'
  }`;

/**
 * Sign one admission packet document in Pelora (Phase 19 F18). Full screen; on a landscape tablet
 * the template text is on the left and the signer and signature on the right. The server renders
 * the PDF from the template as signed and records the logged-in user as the witness.
 */
const SignPacketDocumentSheet: React.FC<SignPacketDocumentSheetProps> = ({
  open, patientId, item, template, guardians, patientDob, patientName = '', witnessName, onClose, onSigned,
}) => {
  const [signerId, setSignerId] = useState<string>('');
  const [patientSignerName, setPatientSignerName] = useState(patientName);
  const [service, setService] = useState<PacketService | ''>('');
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSignerId('');
    setPatientSignerName(patientName);
    setService(item?.service ?? '');
    setSignature(null);
    setError(null);
    setBusy(false);
  }, [open, item, patientName]);

  if (!item) return null;

  const isConsentType = CONSENT_PACKET_TYPES.includes(item.type);
  const needsServiceChoice = Boolean(template?.perService) && !item.service;
  const age = ageInYears(patientDob);
  const patientIsMinor = age !== null && age < 18;
  const title = template?.title || PATIENT_DOCUMENT_TYPE_LABELS[item.type];
  const fullTitle = `${title}${item.service ? ` (${item.service})` : ''}`;

  const signerInput = (): AdmissionPacketSignerInput | null => {
    if (signerId === PATIENT_SIGNER) return patientSignerName.trim() ? { signerType: 'PATIENT', name: patientSignerName.trim() } : null;
    return signerId ? { guardianId: signerId } : null;
  };

  const canSubmit = !busy && Boolean(signerInput()) && Boolean(signature) && (!needsServiceChoice || Boolean(service));

  const submit = async () => {
    const signer = signerInput();
    if (!signer) { setError('Choose who is signing.'); return; }
    if (!signature) { setError('Draw the signature inside the box.'); return; }
    const svc = item.service ?? (service || undefined);
    if (template?.perService && !svc) { setError('Choose the service this consent covers.'); return; }
    setBusy(true);
    setError(null);
    try {
      const doc = await PatientDocumentsService.signAdmissionDocument(patientId, {
        mode: 'SIGN',
        type: item.type,
        ...(template?.perService && svc ? { service: svc } : {}),
        signer,
        signatureBase64: signature,
      });
      onSigned(doc);
    } catch (err) {
      setError(signErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      title={`Sign: ${fullTitle}`}
      subtitle={template ? `Version ${template.version}${template.isDefaultText ? ' · Pelora default text' : ''}` : undefined}
      width="full"
      dismissible={!busy}
      footer={(
        <div className="flex gap-2 md:justify-end">
          <button type="button" onClick={onClose} disabled={busy} className="min-h-[44px] flex-1 rounded-lg border border-slate-300 px-5 text-base font-medium text-slate-700 md:flex-none">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="flex min-h-[44px] flex-[2] items-center justify-center gap-2 rounded-lg bg-slate-800 px-6 text-base font-semibold text-white disabled:opacity-50 md:flex-none"
          >
            {busy ? <><Loader size={18} className="animate-spin" />Signing…</> : 'Submit signature'}
          </button>
        </div>
      )}
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 md:landscape:grid-cols-2">
        <section aria-label="Document text" className="min-h-0">
          <h3 className="mb-2 text-base font-semibold text-slate-900">{fullTitle}</h3>
          <div
            className="max-h-[40vh] overflow-y-auto whitespace-pre-line rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-800 lg:max-h-[65vh] md:landscape:max-h-[65vh]"
            tabIndex={0}
            data-testid="packet-template-body"
          >
            {template?.body || 'The document text is not available.'}
          </div>
        </section>

        <section aria-label="Signer and signature" className="space-y-4">
          {needsServiceChoice && (
            <label className="block text-sm font-medium text-slate-700">
              Service this consent covers
              <select
                aria-label="Service"
                value={service}
                onChange={(e) => setService(e.target.value as PacketService | '')}
                className="mt-1 min-h-[44px] w-full rounded-md border border-slate-300 px-3 text-base"
                disabled={busy}
              >
                <option value="">Choose a service</option>
                {PACKET_SERVICES.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
              </select>
            </label>
          )}

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-slate-700">Who is signing</legend>
            <div className="space-y-2" role="radiogroup">
              {guardians.length === 0 && (
                <p className="text-sm text-slate-500">No parent, guardian or legal representative is recorded. Add one under Legal guardians.</p>
              )}
              {guardians.map((g) => {
                const blocked = isConsentType && !g.canConsent;
                const active = signerId === g.guardianId;
                return (
                  <button
                    key={g.guardianId}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={blocked || busy}
                    onClick={() => setSignerId(g.guardianId)}
                    className={choiceClass(active, blocked)}
                    data-testid={`signer-${g.guardianId}`}
                  >
                    <span className={`mt-1 h-4 w-4 flex-shrink-0 rounded-full border-2 ${active ? 'border-slate-800 bg-slate-800' : 'border-slate-400'}`} aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{g.name}</span>
                      <span className="block text-xs text-slate-600">
                        {g.relationship || 'Relationship not recorded'} · Legal authority: {authorityLabel(g.legalAuthority)}
                      </span>
                      {blocked && (
                        <span className="mt-0.5 block text-xs text-amber-800">Not recorded as able to consent for the patient</span>
                      )}
                    </span>
                  </button>
                );
              })}
              <button
                type="button"
                role="radio"
                aria-checked={signerId === PATIENT_SIGNER}
                disabled={patientIsMinor || busy}
                onClick={() => setSignerId(PATIENT_SIGNER)}
                className={choiceClass(signerId === PATIENT_SIGNER, patientIsMinor)}
                data-testid="signer-patient"
              >
                <span className={`mt-1 h-4 w-4 flex-shrink-0 rounded-full border-2 ${signerId === PATIENT_SIGNER ? 'border-slate-800 bg-slate-800' : 'border-slate-400'}`} aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900">Patient (adult)</span>
                  <span className="block text-xs text-slate-600">The patient signs for themself</span>
                  {patientIsMinor && <span className="mt-0.5 block text-xs text-amber-800">The patient is under 18; a parent, guardian or managing conservator signs</span>}
                </span>
              </button>
            </div>
          </fieldset>

          {signerId === PATIENT_SIGNER && (
            <label className="block text-sm font-medium text-slate-700">
              Patient&apos;s name as signed
              <input
                aria-label="Patient's name as signed"
                type="text"
                value={patientSignerName}
                maxLength={120}
                onChange={(e) => setPatientSignerName(e.target.value)}
                className="mt-1 min-h-[44px] w-full rounded-md border border-slate-300 px-3 text-base"
                disabled={busy}
              />
            </label>
          )}

          <SignaturePad label="Signature" value={signature} onChange={setSignature} active={open} disabled={busy} />

          <p className="text-sm text-slate-600">
            Witness: <span className="font-medium text-slate-800">{witnessName}</span>
          </p>

          {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
        </section>
      </div>
    </Sheet>
  );
};

export default SignPacketDocumentSheet;
